import { ReactNode, useEffect, useRef, useState } from "react";

// DEPENDENCIES
import QRCode from "qrcode";
import { useLocation } from "react-router-dom";
import {
  DocumentData,
  DocumentReference,
  Firestore,
  deleteField,
  getFirestore,
  onSnapshot,
  setDoc,
  Timestamp,
} from "@firebase/firestore";
import * as Sentry from "@sentry/react";
import axios from "axios";
import moment from "moment";

// UTILS
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { logAnalyticsAndHotjarEvent } from "../../main";
import { FIRESTORE_KYC_DOC_TTL_DAYS } from "../../constants";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";
import {
  onUpdateQrImage,
  onUpdateScannedQr,
  onUpdateSuccessMessage,
} from "../../reducers/qrReducer";
import { onUpdateCompletedRemote } from "../../reducers/onboardingDataReducer";
import useForm from "../../hooks/useForm";
import useMobileLayout from "../../hooks/useMobileLayout";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import useRemoteParams from "../../hooks/useRemoteParams";
import {
  DNI_SCAN_POLLING_INTERVAL_MS,
  DNI_SCAN_POLLING_TIMEOUT_MS,
} from "../../constants";
import {
  genericToastifyError,
  getCompanyPersonType,
  qrStyle,
} from "../../utils";
import {
  DniScanValidation,
  getDniScanValidation,
  getRenaperData,
  validateDni,
} from "../../connectors/connectors";
import { updateRenaperData } from "../../reducers/renaperReducer";
import {
  base64ToFile,
  fileToBase64,
  getRenaperInformation,
  showCustomToastifyError,
  waitForFirebaseLogin,
} from "../../app/utils";
import useTheme from "../../hooks/useTheme";

// TYPES
import {
  ComponentProps,
  DniImageType,
  FirebaseDniData,
  OnboardingType,
} from "../../types";

// COMPONENTS
import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle";
import { TextField } from "@allaria-tech/matter/TextField";
import Layout from "../Layout/Layout";
import FormContainer from "../FormContainer/FormContainer";
import Container from "../Container/Container";
import UploadDniForm from "./UploadDniForm";
import UploadErrorCopyLink from "./UploadErrorCopyLink";
import ScannerDni, {
  ScannerActions,
  ScannerView,
} from "../ScannerDni/ScannerDni";
import { Alert } from "@allaria-tech/matter/Alert";
import { Feedback } from "@allaria-tech/matter/Feedback";
import { Typography } from "@allaria-tech/matter/Typography";

// STYLES
import styles from "./UploadDniCard.module.scss";

export type DniStep =
  | "qr"
  | "scan"
  | "upload_front"
  | "upload_back"
  | "success";

type DniFile = { name: string; file: File } | null;
interface DniFilesState {
  frontDNI: DniFile;
  backDNI: DniFile;
}
interface UploadDniCardProps extends ComponentProps {
  getDniRef: ({
    db,
    uuid,
    email,
  }: {
    db: Firestore;
    uuid: string;
    email: string;
  }) => DocumentReference<DocumentData, DocumentData>;

  showCopyLink?: boolean;
  onUploadDni: (
    type: DniImageType,
    file: File,
    uuid: string,
    cuit: string,
  ) => Promise<void>;
  currentDniStep?: DniStep;
  onCustomScanDni?: (
    front: { name: string; base64: string },
    back: { name: string; base64: string },
    uuid: string,
    dniRef: DocumentReference<DocumentData, DocumentData>,
  ) => Promise<void>;
  onUploadDniFallback?: (
    front: { name: string; base64: string },
    back: { name: string; base64: string },
    uuid: string,
  ) => Promise<void>;
  onCustomScanError?: (error: unknown) => void;
  testId?: string;
  onUploadDniFallbackError?: (error: unknown) => void;
  qrDescription?: ReactNode;
  onClose?: () => void;
  closeText?: string;
}

const isFinalScanStatus = (status?: DniScanValidation["status"]) =>
  status === "OK" || status === "FAILED" || status === "CHECK";

const getFailedScanOrThrow = (error: unknown): DniScanValidation => {
  const failedScan = axios.isAxiosError<DniScanValidation>(error)
    ? error.response?.data
    : undefined;
  if (failedScan?.status === "FAILED") return failedScan;
  throw error;
};

const waitForDniScanResult = async (): Promise<DniScanValidation> => {
  const deadline = Date.now() + DNI_SCAN_POLLING_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await new Promise((resolve) =>
      setTimeout(resolve, DNI_SCAN_POLLING_INTERVAL_MS),
    );
    const data = await getDniScanValidation()
      .then((response) => response.data)
      .catch(getFailedScanOrThrow);
    if (isFinalScanStatus(data?.status)) return data;
  }

  throw new Error("DNI_SCAN_POLLING_TIMEOUT");
};

const isRenaperUnavailable = (scan?: DniScanValidation | null) =>
  scan?.status === "FAILED" &&
  /RENAPER unreachable/i.test(scan.errorDetails?.detail ?? "");

const UploadDniCard = ({
  formik,
  getDniRef,
  showCopyLink,
  onUploadDni,
  currentDniStep,
  onCustomScanDni,
  onUploadDniFallback,
  onCustomScanError,
  testId = "upload-dni-card",
  onUploadDniFallbackError,
  qrDescription,
  onClose,
  closeText = "Cerrar",
}: UploadDniCardProps) => {
  const {
    section,
    successContainer,
    successCard,
    loading,
    layoutContainer,
    buttonsContainer,
    copyLinkContainer,
    toastErrorLink,
    chooseMethodContainer,
    cryptoChooseMethod,
    qrContainer,
    uploadErrorAlert: uploadErrorAlertStyle,
  } = styles;

  const [fileDropError, setFileDropError] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<
    "PHOTO" | "RENAPER_UNAVAILABLE" | null
  >(null);
  const [showDesktopInputCopy, setShowDesktopInputCopy] =
    useState<boolean>(false);

  const [dniData, setDniData] = useState<FirebaseDniData>({
    scannedQR: false,
    dniScanValidation: "",
    completedRemote: false,
  });
  const [dniFiles, setDniFilesState] = useState<DniFilesState>(
    () =>
      (formik?.values?.dniFiles as DniFilesState | undefined) ?? {
        frontDNI: null,
        backDNI: null,
      },
  );
  const setDniFiles = (value: DniFilesState) => {
    setDniFilesState(value);
    formik?.setFieldValue("dniFiles", value);
  };
  const setSideFile = (imageType: DniImageType, value: DniFile) => {
    if (value) setUploadError(null);
    setDniFiles({ ...dniFiles, [imageType]: value });
  };

  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false);
  const [isScanPending, setIsScanPending] = useState<boolean>(false);
  const [resumedScan, setResumedScan] = useState<DniScanValidation | null>(
    null,
  );
  const [isAdvancingFromQr, setIsAdvancingFromQr] = useState<boolean>(false);
  const hasAdvancedRef = useRef(false);
  const isNavigatingBackRef = useRef(false);
  const [step, setStep] = useState<DniStep | null>(null);
  const [scanner, setScanner] = useState<{
    view: ScannerView;
    actions: ScannerActions;
  } | null>(null);

  const side: DniImageType = step === "upload_back" ? "backDNI" : "frontDNI";

  const dispatch = useAppDispatch();
  const { qrImage } = useAppSelector((state) => state.qr);
  const { isNavigating } = useAppSelector((state) => state.buttons);
  const theme = useTheme();

  const isScanStep = step === "scan";

  const { handleContinue, handleBack: handleBackDefault } = useForm(formik);

  // Remote
  const location = useLocation();
  const isRemoteStep = location.pathname.includes("dni");

  const { remoteUrlParams, values } = useRemoteParams();
  const { continueWithQR, uuid, email, cuit, onboardingType } = values;
  const { isCrypto, isMobile } = useMobileLayout();

  const dniLink = `${window.location.origin}/dni?${remoteUrlParams.toString()}`;

  // Firestore
  const db = getFirestore();
  const dniRef = getDniRef({ db, uuid: uuid, email: email });

  const isDniStepDone =
    dniData.dniScanValidation === "OK" || !!dniData.fallbackUploaded;

  const currentSideHasFile = !!dniFiles[side];
  const disableContinue = !currentSideHasFile;

  const isScanInFlight = isLoadingButton || isScanPending;

  const disablePrimaryButton = disableContinue || isScanInFlight;

  const isUploading = isScanInFlight || isNavigating;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(dniLink);
    showToastMessage("Link copiado al portapapeles.", {
      containerId: "layoutPage",
      type: "SUCCESS",
    });
  };

  const goToPreviousSubStep = () => {
    isNavigatingBackRef.current = true;
    handleBackDefault();
  };

  const goToScannerView = () => {
    hasAdvancedRef.current = isDniStepDone;
    setStep(isMobile ? "scan" : "qr");
  };

  const onBack = () => {
    if (step === "upload_back") {
      if (isRemoteStep) setStep("upload_front");
      else goToPreviousSubStep();
      return;
    }
    if (step === "upload_front") {
      if (isRemoteStep) return;
      if (!currentDniStep && dniData.scanAttemptUsed) return;
      if (!currentDniStep) goToScannerView();
      else goToPreviousSubStep();
      return;
    }
    goToPreviousSubStep();
  };

  const goToManualUpload = () => {
    setDniFiles({ frontDNI: null, backDNI: null });
    setStep("upload_front");
  };

  const completeRemoteStep = () => {
    setStep("success");
    setDoc(dniRef, { completedRemote: true }, { merge: true });
  };

  const handleShowDesktopUpload = () => setShowDesktopInputCopy(true);

  const handleFileDropError = () => {
    setFileDropError(true);
    showToastMessage(
      <span>
        Ocurrió un problema con la carga de imágenes. Intentá nuevamente más
        tarde o{" "}
        {isMobile ? (
          "continuá desde un ordenador"
        ) : (
          <span className={toastErrorLink} onClick={handleShowDesktopUpload}>
            continuá desde un ordenador
          </span>
        )}
      </span>,
      {
        containerId: isMobile ? "formAlert" : "layoutPage",
        type: isMobile ? "ERROR" : "INFO",
      },
    );
  };

  const withRenaperErrorToast = async <T,>(request: () => Promise<T>) => {
    try {
      return await request();
    } catch (error) {
      showToastMessage(genericToastifyError, {
        containerId: isMobile ? "formAlert" : "layoutPage",
        type: "ERROR",
      });
      throw error;
    }
  };

  const getUploadableFile = (imageType: DniImageType): DniFile => {
    const picked = dniFiles[imageType];
    if (!picked?.file) return null;
    if (picked.file instanceof File) return picked;

    const serialized = picked.file as unknown as {
      base64String?: string;
      type?: string;
    };
    if (!serialized.base64String) return null;

    return {
      name: picked.name,
      file: base64ToFile(serialized.base64String, picked.name, serialized.type),
    };
  };

  const handleContinueUpload = async () => {
    if (step === "upload_front") {
      if (isRemoteStep) setStep("upload_back");
      else handleContinue();
      return;
    }

    try {
      setIsLoadingButton(true);

      const front = getUploadableFile("frontDNI");
      const back = getUploadableFile("backDNI");

      if (!front || !back) {
        throw new Error("Some dni files missing");
      }
      let scannedNow = false;
      if (dniData.dniScanValidation !== "OK" && !dniData.fallbackUploaded) {
        scannedNow = await onScanDni(front, back, "upload");
      }
      if (continueWithQR) setDoc(dniRef, { scannedQR: false }, { merge: true });
      if (isRemoteStep) completeRemoteStep();
      else if (scannedNow) {
        hasAdvancedRef.current = true;
        await continueWithRenaperData();
      } else handleContinue();
    } catch (error) {
      console.log(error);
      if (!onCustomScanError) onBack();
    } finally {
      setIsLoadingButton(false);
    }
  };

  const handleScanComplete = async (
    dniFront: { name: string; file: File },
    dniBack: { name: string; file: File },
  ) => {
    setDniFiles({ frontDNI: dniFront, backDNI: dniBack });
    // Let failures propagate so ScannerDni can show the error screen and fall
    // back to the manual upload flow.
    await onScanDni(dniFront, dniBack, "scan");
    if (continueWithQR) setDoc(dniRef, { scannedQR: false }, { merge: true });
    if (isRemoteStep) completeRemoteStep();
  };

  const onScanDni = async (
    dniFront: {
      name: string;
      file: File;
    },
    dniBack: {
      name: string;
      file: File;
    },
    mode: "scan" | "upload",
  ): Promise<boolean> => {
    if (!uuid) throw new Error("UUID is missing");

    const front = {
      name: dniFront.name,
      base64: await fileToBase64(dniFront.file),
    };
    const back = {
      name: dniBack.name,
      base64: await fileToBase64(dniBack.file),
    };

    try {
      if (onCustomScanDni) {
        await onCustomScanDni(front, back, uuid, dniRef);
        if (isRemoteStep || mode === "scan") {
          setDoc(dniRef, { completedRemote: true }, { merge: true });
        }
        return true;
      }

      const isAdditionalSigner =
        onboardingType === "ADDITIONAL_COOWNER" ||
        onboardingType === "SIGNATORY";

      if (isAdditionalSigner && !cuit) throw new Error("CUIT is missing");

      const companyPersonType = getCompanyPersonType(
        onboardingType as OnboardingType,
      );

      const scanRequest: DniScanValidation | undefined = await validateDni({
        companyPersonType,
        frontImage: front.base64,
        backImage: back.base64,
        captureMethod: mode === "scan" ? "SCAN" : "UPLOAD",
        ...(isAdditionalSigner ? { cuit } : {}),
      })
        .then((response) => response.data)
        .catch(getFailedScanOrThrow);

      const data: DniScanValidation | undefined = isFinalScanStatus(
        scanRequest?.status,
      )
        ? scanRequest
        : await waitForDniScanResult();

      if (!data || !data.status || ["FAILED", "CHECK"].includes(data.status)) {
        if (data?.errorDetails?.code === "CUIT_NOT_CONFIRMED")
          throw new Error("CUIT_NOT_CONFIRMED");

        if (isRenaperUnavailable(data)) throw new Error("RENAPER_UNAVAILABLE");

        Sentry.captureException(new Error(), {
          extra: { status: data?.status },
        });

        if (data?.status === "CHECK") {
          const { data: renaperData } =
            await withRenaperErrorToast(getRenaperData);
          const statusDetail = JSON.parse(
            renaperData.validations[0]?.statusDetail,
          );
          if (
            statusDetail.code === "10001" &&
            statusDetail.message === "Exito" &&
            statusDetail.valid === "No vigente"
          ) {
            throw new Error("DNI_NO_VIGENTE");
          }
        }
        throw new Error("FAILED at validate dni");
      } else if (data.status === "OK") {
        setDoc(
          dniRef,
          {
            dniScanValidation: data.status,
            ...((isRemoteStep || mode === "scan") && { completedRemote: true }),
          },
          { merge: true },
        );
        return true;
      }
    } catch (error) {
      const isCuitNotConfirmed =
        error instanceof Error && error.message === "CUIT_NOT_CONFIRMED";

      const isMaxAttemptsReached =
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response.data?.code === "MAX_ATTEMPTS_REACHED";

      if (isCuitNotConfirmed) showCustomToastifyError("CUIT_NOT_CONFIRMED");
      else if (!isMaxAttemptsReached) Sentry.captureException(error);

      if (mode === "scan") {
        setDoc(dniRef, { scanAttemptUsed: true }, { merge: true });
        throw error;
      }

      if (isMaxAttemptsReached) {
        try {
          if (onUploadDniFallback) {
            await onUploadDniFallback(front, back, uuid);
          } else if (dniFront.file && dniBack.file) {
            await onUploadDni("frontDNI", dniFront.file, uuid, cuit);
            await onUploadDni("backDNI", dniBack.file, uuid, cuit);
          }
          await setDoc(dniRef, { fallbackUploaded: true }, { merge: true });
        } catch (fallbackError) {
          Sentry.captureException(fallbackError);
          onUploadDniFallbackError?.(fallbackError);
          throw fallbackError;
        }
      } else {
        if (onCustomScanError) {
          onCustomScanError?.(error);
          throw error;
        }
        const isRenaperDown =
          error instanceof Error && error.message === "RENAPER_UNAVAILABLE";

        if (!isRenaperDown) setDniFiles({ frontDNI: null, backDNI: null });
        setUploadError(
          isCuitNotConfirmed
            ? null
            : isRenaperDown
              ? "RENAPER_UNAVAILABLE"
              : "PHOTO",
        );
        throw error;
      }
    }
    return false;
  };

  const dniStep = {
    name: "Identidad",
    step: 1,
    subSteps: [{ step: 1 }, { step: 2 }],
  };

  const remoteSubStep = step === "upload_back" ? 2 : 1;

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;

    waitForFirebaseLogin().then(() => {
      if (cancelled) return;
      unsub = subscribeToDniDoc();
    });

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, []);

  const subscribeToDniDoc = () =>
    onSnapshot(dniRef, (document) => {
      if (document.exists()) {
        const data = document.data() as FirebaseDniData;
        setDniData((prevState) => ({
          ...prevState,
          ...data,
        }));
        if (data.scannedQR) dispatch(onUpdateScannedQr(true));
        if (data.completedRemote) dispatch(onUpdateCompletedRemote(true));
        setIsLoadingData(false);
      } else {
        // `expiresAt` lo consume la Cloud Function programada `cleanupRenaper`, que borra
        // los documentos vencidos (drafts abandonados) con la PII de RENAPER que pudieran tener.
        setDoc(
          dniRef,
          {
            ...dniData,
            expiresAt: Timestamp.fromDate(
              moment().add(FIRESTORE_KYC_DOC_TTL_DAYS, "days").toDate(),
            ),
          },
          { merge: true },
        );
      }
    });

  useEffect(() => {
    if (onCustomScanDni) return;
    let cancelled = false;

    const resumePendingScan = async () => {
      const isPending = await getDniScanValidation()
        .then(({ data }) => data?.status === "IN_PROGRESS")
        .catch(() => false);
      if (!isPending || cancelled) return;

      setIsScanPending(true);
      try {
        const result = await waitForDniScanResult();
        if (!cancelled) setResumedScan(result);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        if (!cancelled) setIsScanPending(false);
      }
    };

    resumePendingScan();
    return () => {
      cancelled = true;
    };
  }, []);

  // Set QR information
  useEffect(() => {
    if (isCrypto) {
      const { token } = values;
      setDoc(dniRef, { remoteToken: token }, { merge: true });
      return () => {
        setDoc(dniRef, { remoteToken: deleteField() }, { merge: true });
      };
    }
  }, []);

  useEffect(() => {
    QRCode.toDataURL(dniLink.concat(`&withQR=true`), qrStyle(theme)).then(
      (img) => dispatch(onUpdateQrImage(img)),
    );

    dispatch(
      onUpdateSuccessMessage("Ya podés cargar las fotos desde tu teléfono."),
    );
  }, []);

  // Update QR modal
  useEffect(() => {
    if (continueWithQR && !isLoadingData) {
      logAnalyticsAndHotjarEvent("qr_code_scanned_dni");
      setDoc(dniRef, { scannedQR: true }, { merge: true });
    }
  }, [continueWithQR, isLoadingData]);

  useEffect(() => {
    // The same card instance is reused across the identity substeps, so the
    // guard has to reopen whenever the parent moves it to another one.
    hasAdvancedRef.current = isNavigatingBackRef.current && isDniStepDone;
    isNavigatingBackRef.current = false;
    if (!isRemoteStep && currentDniStep) setStep(currentDniStep);
    else if (isMobile) setStep("scan");
    else setStep(isRemoteStep ? "upload_front" : "qr");
  }, [currentDniStep, isRemoteStep]);

  useEffect(() => {
    if (step === "scan" && dniData.scanAttemptUsed && !isDniStepDone)
      goToManualUpload();
  }, [step, dniData.scanAttemptUsed]);

  const continueWithRenaperData = async () => {
    if (!uuid) throw new Error("UUID is missing");

    // A custom scan (Allaria Digital) is validated by its own backend, which
    // returns the RENAPER data itself: midgard has no validation to read, and
    // the step's onContinue decides where to go.
    if (onCustomScanDni) {
      handleContinue();
      return;
    }

    const { informationParsed, externalCode } = await withRenaperErrorToast(
      getRenaperInformation,
    );
    const { expirationDate } = informationParsed;

    if (moment(expirationDate, "DD-MM-YYYY").isBefore(moment(), "day")) {
      showCustomToastifyError("DNI_NO_VIGENTE");
      throw new Error("DNI_NO_VIGENTE");
    }

    dispatch(
      updateRenaperData({
        ...informationParsed,
        externalCode,
      }),
    );
    handleContinue();
  };

  useEffect(() => {
    if (!resumedScan) return;

    if (resumedScan.status === "OK") {
      if (!isRemoteStep) hasAdvancedRef.current = true;
      setDoc(
        dniRef,
        {
          dniScanValidation: resumedScan.status,
          ...(isRemoteStep && { completedRemote: true }),
        },
        { merge: true },
      );
      if (isRemoteStep) return;
      continueWithRenaperData().catch(Sentry.captureException);
      return;
    }

    if (resumedScan.errorDetails?.code === "CUIT_NOT_CONFIRMED")
      showCustomToastifyError("CUIT_NOT_CONFIRMED");
    else
      setUploadError(
        isRenaperUnavailable(resumedScan) ? "RENAPER_UNAVAILABLE" : "PHOTO",
      );
    setDniFiles({ frontDNI: null, backDNI: null });
  }, [resumedScan]);

  useEffect(() => {
    if (!isDniStepDone || hasAdvancedRef.current) return;
    // The phone saves its attempts before it marks the step completed: the
    // guard closes only once this side can move on, or the snapshot with the
    // attempts alone would keep it on the QR for good.
    if (
      !isRemoteStep &&
      !dniData.completedRemote &&
      dniData.dniScanValidation !== "OK"
    )
      return;
    hasAdvancedRef.current = true;

    if (isRemoteStep) {
      completeRemoteStep();
    } else {
      if (step === "qr") setIsAdvancingFromQr(true);
      if (dniData.dniScanValidation !== "OK") {
        handleContinue();
        return;
      }
      continueWithRenaperData().catch((error) => {
        Sentry.captureException(error);
        setIsAdvancingFromQr(false);
      });
    }
  }, [dniData, isRemoteStep, currentDniStep, step]);

  useEffect(() => {
    if (isRemoteStep) return;

    // ScannerDni only renders its own footer in the remote flow, so here we
    // mirror it onto the step buttons.
    if (isScanStep) {
      const view = scanner?.view ?? "initial";
      const isError = view === "error";

      const openManualUpload = () => {
        scanner?.actions.uploadImages();
        handleContinue();
      };

      dispatch(
        onUpdateTertiaryButton(
          onClose && view === "initial"
            ? {
                text: closeText,
                variant: undefined,
                disabled: false,
                isLoading: false,
                onClick: onClose,
                show: true,
              }
            : { show: false, variant: undefined },
        ),
      );
      dispatch(
        onUpdateSecondaryButton({
          text: "Cargar imágenes",
          disabled: isScanPending,
          isLoading: false,
          onClick: openManualUpload,
          show: view === "initial",
        }),
      );
      dispatch(
        onUpdatePrimaryButton({
          text: isError ? "Aceptar" : "Abrir cámara",
          disabled: isScanPending,
          isLoading: isScanPending,
          onClick: isError ? openManualUpload : scanner?.actions.openCamera,
          show: view === "initial" || isError,
        }),
      );
      return;
    }

    const isUploadStep = step === "upload_front" || step === "upload_back";

    if (onClose && !isUploadStep) {
      const disabled = isAdvancingFromQr || isLoadingButton;
      dispatch(onUpdateTertiaryButton({ show: false, variant: undefined }));
      dispatch(
        onUpdateSecondaryButton({
          text: closeText,
          disabled,
          isLoading: false,
          onClick: onClose,
          show: true,
        }),
      );
      dispatch(
        onUpdatePrimaryButton({
          text: "Subir imágenes",
          disabled,
          isLoading: false,
          onClick: handleContinue,
          show: true,
        }),
      );
      return;
    }

    dispatch(
      onUpdateTertiaryButton({
        text: "Subir imágenes",
        variant: undefined,
        disabled: isAdvancingFromQr || isScanInFlight,
        isLoading: false,
        onClick: handleContinue,
        show: !isUploadStep,
      }),
    );
    dispatch(
      onUpdateSecondaryButton({
        text: "Volver",
        disabled: isScanInFlight,
        isLoading: false,
        onClick: onBack,
        show:
          isUploadStep &&
          !(
            step === "upload_front" &&
            !currentDniStep &&
            dniData.scanAttemptUsed
          ),
      }),
    );
    dispatch(
      onUpdatePrimaryButton({
        text: "Continuar",
        disabled: disablePrimaryButton,
        isLoading: isScanInFlight,
        onClick: handleContinueUpload,
        show: isUploadStep,
      }),
    );
  }, [
    dniData,
    disablePrimaryButton,
    isLoadingButton,
    isScanPending,
    isRemoteStep,
    isScanStep,
    scanner,
    step,
    isAdvancingFromQr,
    // The card is reused across the identity substeps: the buttons must take
    // the substep's props, or Volver on the front photo keeps the scanner's
    // onBack and opens the scanner inside the front photo's substep.
    currentDniStep,
    onClose,
    closeText,
  ]);

  if (isLoadingData)
    return (
      <div className={loading} data-testid={`${testId}-loading`}>
        <LoadingCircle testId={`${testId}-loader`} />
      </div>
    );

  const uploadErrorAlert = uploadError && step === "upload_front" && (
    <Alert
      variant="ERROR"
      className={uploadErrorAlertStyle}
      testId={`${testId}-upload-error`}
    >
      {uploadError === "RENAPER_UNAVAILABLE"
        ? "No se pudo validar el DNI, por favor intentá más tarde"
        : "Volvé a cargar la foto del DNI asegurándote de que esté bien iluminada y sin reflejos."}
    </Alert>
  );

  const renderStep = () => {
    if (isScanStep)
      return (
        <ScannerDni
          onScanDni={handleScanComplete}
          onUploadImages={goToManualUpload}
          isRemoteStep={isRemoteStep}
          showBackHeader={isCrypto && isMobile}
          onViewChange={(view, actions) => setScanner({ view, actions })}
          testId={`${testId}-scanner`}
        />
      );

    if (isRemoteStep && step === "success")
      return (
        <Layout
          hideStepper
          hideSupportButton
          testId={`${testId}-success-layout`}
        >
          <Container className={`${layoutContainer} ${successContainer}`}>
            <Feedback
              inline
              type="SUCCESS"
              title="¡Verificamos tu DNI!"
              description="Podés cerrar esta ventana y seguir con la apertura de tu cuenta."
              className={successCard}
            />
          </Container>
        </Layout>
      );

    if (isRemoteStep)
      return (
        <Layout hideStepper testId={`${testId}-layout`}>
          <Container className={layoutContainer}>
            <FormContainer currentStep={dniStep} currentSubStep={remoteSubStep}>
              <UploadDniForm
                side={side}
                onFileDropError={handleFileDropError}
                isExternalLink={isRemoteStep}
                isLoading={isUploading}
                isMobile={isMobile}
                selectedFile={dniFiles[side]?.file ?? null}
                setSideFile={setSideFile}
                testId={`${testId}-form`}
              />
              {uploadErrorAlert}
              <UploadErrorCopyLink
                isMobile={isMobile}
                dniLink={dniLink}
                onCopyLink={handleCopyLink}
                fileDropError={fileDropError}
                showDesktopInputCopy={showDesktopInputCopy}
                testId={`${testId}-error-copy-link`}
              />
            </FormContainer>
          </Container>
          <ButtonGroup
            container
            containerClassName={buttonsContainer}
            secondary={
              step === "upload_back"
                ? {
                    text: "Volver",
                    isDisabled: isScanInFlight,
                    onClick: onBack,
                    testId: `${testId}-back`,
                  }
                : undefined
            }
            primary={{
              text: "Continuar",
              isDisabled: disablePrimaryButton,
              isLoading: isScanInFlight,
              onClick: handleContinueUpload,
              testId: `${testId}-continue`,
            }}
          />
        </Layout>
      );

    if (step === "upload_front" || step === "upload_back")
      return (
        <>
          <UploadDniForm
            side={side}
            onFileDropError={handleFileDropError}
            isExternalLink={isRemoteStep}
            isLoading={isUploading}
            isMobile={isMobile}
            isCrypto={isCrypto}
            selectedFile={dniFiles[side]?.file ?? null}
            setSideFile={setSideFile}
            testId={`${testId}-form`}
          />

          {uploadErrorAlert}

          {showCopyLink && !fileDropError && step !== "upload_back" && (
            <div
              className={copyLinkContainer}
              data-testid={`${testId}-copy-link`}
            >
              <h4>
                O compartí el siguiente link y esperá a que carguen las fotos.
              </h4>
              <TextField
                variant="url"
                value={dniLink}
                onClick={handleCopyLink}
                testId={`${testId}-link`}
              />
            </div>
          )}

          <UploadErrorCopyLink
            isMobile={isMobile}
            dniLink={dniLink}
            onCopyLink={handleCopyLink}
            fileDropError={fileDropError}
            showDesktopInputCopy={showDesktopInputCopy}
            testId={`${testId}-error-copy-link`}
          />
        </>
      );

    if (step === "qr") {
      if (isAdvancingFromQr)
        return (
          <div className={loading} data-testid={`${testId}-loading`}>
            <LoadingCircle testId={`${testId}-loader`} />
          </div>
        );

      return (
        <div
          className={`${chooseMethodContainer} ${isCrypto ? cryptoChooseMethod : ""}`}
          data-testid={`${testId}-qr`}
        >
          <>
            {qrDescription ?? (
              <Typography type="BODY" size="M" weight="BASE">
                Escaneá el código QR para continuar desde tu celular.
              </Typography>
            )}
            <div className={qrContainer} data-testid={`${testId}-qr-image`}>
              <img src={qrImage} alt="QR" />
            </div>
            <Alert variant="INFO" testId={`${testId}-qr-alert`} width="FIT">
              Para una mejor experiencia, continuá este paso desde tu celular.
            </Alert>
          </>
        </div>
      );
    }

    return null;
  };

  return (
    <section className={isScanStep ? section : ""} data-testid={testId}>
      {renderStep()}
    </section>
  );
};

export default UploadDniCard;
